import { useMemo, useState } from 'react';
import { api, ApiError, formatDateTime } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import type { HygieneInspection, InspectionResultResponse, Page, PublishedCriterion } from '../../lib/types';
import { useApiQuery } from '../../lib/useApi';
import { EmptyState, ErrorNote, Field, Modal, PageHeader, Spinner, StatusBadge } from '../../ui/components';

const STATUSES = ['SCHEDULED', 'COMPLETED', 'CANCELLED'];

/** Inspector queue — packages/modules/m25-public-health/src/routes/inspections.ts. Scheduling happens from the Applications screen (or the Complaints "trigger inspection" action); this screen is the inspector's own queue: score, grade, cancel. */
export function Inspections() {
  const { persona } = useAuth();
  const isInspector = (persona?.roles.includes('PH_INSPECTOR') || persona?.roles.includes('SENIOR_PHI')) ?? false;
  const [status, setStatus] = useState('');
  const [detailId, setDetailId] = useState<string | null>(null);

  const qs = new URLSearchParams({ limit: '50' });
  if (status) qs.set('status', status);
  const listQ = useApiQuery<Page<HygieneInspection>>(`/api/v1/m25/inspections?${qs.toString()}`);

  return (
    <div>
      <PageHeader title="Hygiene Inspections" subtitle="Scored checklist against published criteria — computes the A/B/C grade and can trigger suspension on a critical violation." />

      {!isInspector ? (
        <div className="mb-4">
          <ErrorNote message="Viewing the inspection queue requires PH_INSPECTOR or SENIOR_PHI." />
        </div>
      ) : null}

      <div className="mb-4 flex flex-wrap gap-2">
        <select className="field-input !w-44" value={status} onChange={(e) => setStatus(e.target.value)}>
          <option value="">All statuses</option>
          {STATUSES.map((s) => (
            <option key={s} value={s}>
              {s}
            </option>
          ))}
        </select>
      </div>

      {listQ.error ? <ErrorNote message={listQ.error} /> : null}

      <div className="card overflow-x-auto">
        {listQ.loading ? (
          <Spinner />
        ) : (listQ.data?.items.length ?? 0) === 0 ? (
          <EmptyState title="No inspections match" />
        ) : (
          <table className="w-full">
            <thead>
              <tr>
                <th className="table-head">Premises</th>
                <th className="table-head">District</th>
                <th className="table-head">Source</th>
                <th className="table-head">Status</th>
                <th className="table-head">Grade</th>
                <th className="table-head">When</th>
              </tr>
            </thead>
            <tbody>
              {listQ.data!.items.map((i) => (
                <tr key={i.id} className="cursor-pointer hover:bg-slate-50" onClick={() => setDetailId(i.id)}>
                  <td className="table-cell font-medium">{i.premisesName}</td>
                  <td className="table-cell">{i.district}</td>
                  <td className="table-cell">
                    <StatusBadge status={i.source} />
                  </td>
                  <td className="table-cell">
                    <StatusBadge status={i.status} />
                  </td>
                  <td className="table-cell">{i.grade ? <StatusBadge status={i.grade} /> : '—'}</td>
                  <td className="table-cell text-xs">{formatDateTime(i.scheduledAt ?? i.createdAt)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {detailId ? (
        <InspectionDetailModal id={detailId} isInspector={isInspector} onClose={() => setDetailId(null)} onChanged={() => listQ.reload()} />
      ) : null}
    </div>
  );
}

function InspectionDetailModal({ id, isInspector, onClose, onChanged }: { id: string; isInspector: boolean; onClose: () => void; onChanged: () => void }) {
  const detailQ = useApiQuery<HygieneInspection>(`/api/v1/m25/inspections/${id}`);
  const checklistQ = useApiQuery<{ criteria: PublishedCriterion[] }>('/api/v1/m25/inspections/checklist');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [scores, setScores] = useState<Record<string, string>>({});
  const [notesByCriterion, setNotesByCriterion] = useState<Record<string, string>>({});
  const [criticalViolations, setCriticalViolations] = useState<string[]>([]);
  const [newViolation, setNewViolation] = useState('');
  const [overallNotes, setOverallNotes] = useState('');
  const [result, setResult] = useState<InspectionResultResponse | null>(null);

  const inspection = detailQ.data;
  const criteria = checklistQ.data?.criteria ?? [];
  const maxScoreTotal = useMemo(() => criteria.reduce((s, c) => s + c.maxScore, 0), [criteria]);

  const cancel = async () => {
    setBusy(true);
    setError(null);
    try {
      await api.post(`/api/v1/m25/inspections/${id}/cancel`);
      detailQ.reload();
      onChanged();
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Cancel failed');
    } finally {
      setBusy(false);
    }
  };

  const submitResult = async () => {
    setBusy(true);
    setError(null);
    try {
      const checklist = criteria.map((c) => ({
        criterion: c.code,
        score: Math.min(Number.parseInt(scores[c.code] ?? '0', 10) || 0, c.maxScore),
        notes: notesByCriterion[c.code] || undefined,
      }));
      const res = await api.post<InspectionResultResponse>(`/api/v1/m25/inspections/${id}/result`, {
        checklist,
        criticalViolations,
        notes: overallNotes || undefined,
        photos: [],
      });
      setResult(res);
      detailQ.reload();
      onChanged();
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Could not record result');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title="Hygiene inspection" onClose={onClose} wide>
      {detailQ.loading || !inspection ? (
        <Spinner />
      ) : (
        <div className="space-y-4 text-sm">
          {error ? <ErrorNote message={error} /> : null}
          <div className="flex flex-wrap items-center gap-2">
            <StatusBadge status={inspection.source} />
            <StatusBadge status={inspection.status} />
            {inspection.grade ? <StatusBadge status={inspection.grade} /> : null}
          </div>
          <dl className="grid grid-cols-2 gap-x-6 gap-y-1.5">
            <div className="flex justify-between">
              <dt className="text-slate-500">Premises</dt>
              <dd className="font-medium">{inspection.premisesName}</dd>
            </div>
            <div className="flex justify-between">
              <dt className="text-slate-500">District</dt>
              <dd className="font-medium">{inspection.district}</dd>
            </div>
          </dl>

          {inspection.status === 'COMPLETED' ? (
            <div className="border-t border-slate-100 pt-3">
              <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">
                Recorded result — {inspection.totalScore}/{inspection.maxScore}
              </div>
              <ul className="space-y-1 text-xs">
                {(inspection.checklist ?? []).map((c, i) => (
                  <li key={i} className="flex justify-between rounded bg-slate-50 px-2 py-1">
                    <span>{criteria.find((cr) => cr.code === c.criterion)?.label ?? c.criterion}</span>
                    <span className="font-medium">
                      {c.score}/{c.maxScore}
                    </span>
                  </li>
                ))}
              </ul>
              {inspection.criticalViolations.length > 0 ? (
                <p className="mt-2 text-xs text-rose-600">Critical violations: {inspection.criticalViolations.join('; ')}</p>
              ) : null}
              {inspection.notes ? <p className="mt-2 text-xs text-slate-500">{inspection.notes}</p> : null}
            </div>
          ) : inspection.status === 'SCHEDULED' ? (
            <div className="border-t border-slate-100 pt-3">
              <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">Score checklist ({maxScoreTotal} pts total)</div>
              {checklistQ.loading ? (
                <Spinner />
              ) : (
                <div className="space-y-2">
                  {criteria.map((c) => (
                    <div key={c.code} className="grid grid-cols-[1fr,80px] items-center gap-2">
                      <div className="text-xs text-slate-700">{c.label}</div>
                      <input
                        type="number"
                        min="0"
                        max={c.maxScore}
                        className="field-input !py-1 text-center text-xs"
                        placeholder={`/${c.maxScore}`}
                        value={scores[c.code] ?? ''}
                        onChange={(e) => setScores((s) => ({ ...s, [c.code]: e.target.value }))}
                      />
                    </div>
                  ))}
                  <Field label="Critical violations (if any)">
                    <div className="flex gap-1.5">
                      <input className="field-input" value={newViolation} onChange={(e) => setNewViolation(e.target.value)} placeholder="Describe a critical violation…" />
                      <button
                        className="btn-secondary shrink-0 !px-2.5"
                        disabled={!newViolation.trim()}
                        onClick={() => {
                          setCriticalViolations((v) => [...v, newViolation.trim()]);
                          setNewViolation('');
                        }}
                      >
                        Add
                      </button>
                    </div>
                    {criticalViolations.length > 0 ? (
                      <ul className="mt-1.5 space-y-1">
                        {criticalViolations.map((v, i) => (
                          <li key={i} className="flex items-center justify-between rounded bg-rose-50 px-2 py-1 text-xs text-rose-700">
                            {v}
                            <button onClick={() => setCriticalViolations((vs) => vs.filter((_, xi) => xi !== i))}>✕</button>
                          </li>
                        ))}
                      </ul>
                    ) : null}
                  </Field>
                  <Field label="Overall notes">
                    <textarea className="field-input" rows={2} value={overallNotes} onChange={(e) => setOverallNotes(e.target.value)} />
                  </Field>
                  <div className="flex gap-2">
                    <button className="btn-primary" onClick={submitResult} disabled={busy || !isInspector}>
                      {busy ? 'Submitting…' : 'Submit result'}
                    </button>
                    <button className="btn-danger" onClick={cancel} disabled={busy || !isInspector}>
                      Cancel inspection
                    </button>
                  </div>
                </div>
              )}
            </div>
          ) : null}

          {result ? (
            <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-3 text-xs text-emerald-800">
              Grade computed: {result.grading.grade ?? 'No grade (failed / critical violation)'} — {result.grading.percentage}%
              {result.application ? <span className="ml-2">Application state: {result.application.state}</span> : null}
            </div>
          ) : null}
        </div>
      )}
    </Modal>
  );
}
